import React from 'react'

import {Portal} from 'react-portal'
import {Transition} from 'react-transition-group'

import useKeyPress from '../../hooks/use-key-press'
import useLockScrolling from '../../hooks/use-lock-scrolling'
import cn from '../../libs/class-names'

// This has to be synced with the transition timeouts in the styles
const TRANSITION_TIMEOUT = 0.1 * 1000

export interface Props {
  classNames?: string[]
  children: React.ReactNode
  in?: boolean // To stay compatible with TransitionGroup
  isOpen?: boolean
  lightbox?: boolean
  fullscreenMobile?: boolean
  closeOnOutsideClick?: boolean
  onClose?: () => void
}

const ModalPortal = ({
  classNames,
  children,
  lightbox = false,
  fullscreenMobile = false,
  isOpen = false,
  in: transitionIn = false,
  closeOnOutsideClick = true,
  onClose,
}: Props) => {
  const isActuallyOpen = isOpen || transitionIn
  useLockScrolling(isActuallyOpen)

  useKeyPress(() => {
    if (isActuallyOpen && onClose) {
      onClose()
    }
  }, 'Escape')

  return (
    <Transition
      in={isActuallyOpen}
      appear
      timeout={TRANSITION_TIMEOUT}
      mountOnEnter
      unmountOnExit
      onEnter={(node: HTMLElement) => node && node.scrollTop} // Force first paint before going into entering state!
    >
      {state => (
        <Portal>
          <div
            className={cn(
              'ModalPortal',
              {[state]: !!state, lightbox, fullscreenMobile},
              classNames
            )}
          >
            <div className="ModalPortal__content">{children}</div>
            <div
              className="ModalPortal__background"
              aria-hidden
              onClick={closeOnOutsideClick ? onClose : undefined}
            />
          </div>
        </Portal>
      )}
    </Transition>
  )
}

export default ModalPortal
